import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const [tier, setTier] = useState("");
  const { refreshMe } = useAuth();

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setStatus("missing"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setTier(data.tier || "");
          refreshMe();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= 7) { setStatus("pending"); return; }
        setTimeout(poll, 2000);
      } catch {
        setStatus("error");
      }
    };
    poll();
  }, [params, refreshMe]);

  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      <div className="jj-card p-10 fade-up">
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 text-burgundy animate-spin mx-auto" />
            <h1 className="font-serif text-3xl text-jjblue mt-5">Confirming your blessing…</h1>
            <p className="mt-2 text-[var(--jj-text-secondary)]">One moment while we finalize your membership.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-12 w-12 text-burgundy mx-auto" strokeWidth={1.5} />
            <h1 className="font-serif text-4xl text-jjblue mt-5">Welcome to the {tier} tier</h1>
            <p className="mt-3 text-[var(--jj-text-secondary)]">Thank you for sowing into the community. Your new perks are unlocked.</p>
            <div className="mt-7 flex gap-3 justify-center">
              <Link to="/feed" className="btn-primary" data-testid="success-feed">Go to feed</Link>
              <Link to="/profile" className="btn-secondary" data-testid="success-profile">My profile</Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <h1 className="font-serif text-3xl text-jjblue">Still processing…</h1>
            <p className="mt-3 text-[var(--jj-text-secondary)]">Your payment is being confirmed. We'll upgrade your tier as soon as it clears.</p>
            <Link to="/feed" className="btn-secondary inline-block mt-6">Continue to feed</Link>
          </>
        )}
        {(status === "expired" || status === "error" || status === "missing") && (
          <>
            <h1 className="font-serif text-3xl text-jjblue">Something interrupted the payment</h1>
            <p className="mt-3 text-[var(--jj-text-secondary)]">No worries—try again whenever you're ready.</p>
            <Link to="/pricing" className="btn-primary inline-block mt-6">Back to membership</Link>
          </>
        )}
      </div>
    </div>
  );
}
